/**
 * WHS and site audits (Tendable). Each inspection answers a fixed question
 * set per audit type; the score is the share of questions passed. Issues,
 * actions and recurring findings are all derived from the failed questions,
 * so every number on the WHS page traces back to individual audits.
 * Problem spots: fire-extinguisher tagging and satellite-phone checks fail
 * often, and the two most remote areas are audited rarely and score lower.
 */
import { Quarter, addDays, iso, now, ym } from '../../clock';
import { rng } from '../../rng';
import { SITES } from '../../world';
import { Scope, dateIn, daysInMonth, historyMonths, inScope, memo, round1, scopeOf, syncedAt, trendScopeOf } from './common';

export const AUDIT_TYPES = ['WHS', 'Vehicle Check', 'Site Inspection', 'Infection Control', 'Food Safety'] as const;
export type AuditType = (typeof AUDIT_TYPES)[number];

interface Question {
    question: string;
    category: string;
    fail: number;
}

const q = (category: string, items: [string, number][]): Question[] => items.map(([question, fail]) => ({ question, category, fail }));

const QUESTIONS: Record<AuditType, Question[]> = {
    WHS: [
        ...q('Fire Equipment', [['Fire extinguisher inspection tag is current', 0.3], ['Fire blanket accessible in kitchen', 0.07], ['Exit signs illuminated and unobstructed', 0.05]]),
        ...q('Emergency Procedures', [['Evacuation diagram displayed and current', 0.12], ['Emergency contact list up to date', 0.09], ['Assembly point signed', 0.04]]),
        ...q('First Aid', [['First aid kit stocked and in date', 0.08], ['Snake bite bandages in kit', 0.05]]),
        ...q('Electrical Safety', [['Test and tag current on portable appliances', 0.17], ['No damaged leads or power boards', 0.05]]),
        ...q('Chemical Storage', [['Chemicals stored in a locked cupboard', 0.09], ['Safety data sheet register available', 0.12]]),
        ...q('Heat & Sun Safety', [['Air-conditioning serviced and working', 0.13], ['Drinking water available for staff and clients', 0.02]]),
        ...q('Remote Travel & Communications', [['Satellite phone charged and tested', 0.2], ['Journey management plan on file', 0.1]]),
        ...q('Housekeeping', [['Walkways clear of trip hazards', 0.06], ['Bins emptied and lidded', 0.04]]),
    ],
    'Vehicle Check': [
        ...q('Tyres & Recovery', [['Two spare tyres in good condition', 0.1], ['Recovery kit complete', 0.09]]),
        ...q('Safety Equipment', [['EPIRB registered and in date', 0.14], ['Vehicle first aid kit in date', 0.08], ['Vehicle fire extinguisher tagged', 0.18]]),
        ...q('Servicing & Fluids', [['Service sticker current', 0.1], ['20 litres of drinking water carried', 0.05]]),
        ...q('Cabin & Load', [['Cargo barrier fitted and loads restrained', 0.06], ['Cabin clean and free of loose items', 0.07]]),
    ],
    'Site Inspection': [
        ...q('Security', [['Doors and windows lock securely', 0.09], ['Security screens intact', 0.11]]),
        ...q('Building Condition', [['No water leaks or damp', 0.09], ['Floor coverings safe', 0.06]]),
        ...q('Utilities', [['Hot water system working', 0.07], ['Smoke alarms tested', 0.14]]),
        ...q('Grounds', [['Yard clear of rubbish and hazards', 0.09], ['Dog-proof fencing intact', 0.13]]),
    ],
    'Infection Control': [
        ...q('Hand Hygiene', [['Hand sanitiser available at entry', 0.04], ['Handwashing signage displayed', 0.05]]),
        ...q('Cleaning', [['Cleaning schedule signed off', 0.13], ['Colour-coded cleaning equipment used', 0.09]]),
        ...q('PPE', [['PPE stock adequate', 0.06]]),
        ...q('Waste', [['Sharps container below fill line', 0.05], ['Clinical waste collected on schedule', 0.12]]),
    ],
    'Food Safety': [
        ...q('Temperature Control', [['Fridge temperature at or below 5°C', 0.11], ['Temperature log completed daily', 0.16]]),
        ...q('Storage', [['Food labelled and dated', 0.11], ['Dry store kept off the floor', 0.05]]),
        ...q('Kitchen & Food Safety', [['Food handler certificate on file', 0.09], ['Benches and equipment clean', 0.06]]),
        ...q('Pest Control', [['No evidence of pests', 0.07]]),
    ],
};

interface Area {
    area: string;
    site: string;
    type: AuditType;
    weight: number;
    risk: number; // multiplier on question failure chance
    archivedFrom?: string; // YYYY-MM: no audits from this month on
}

const site = (prefix: string): string => SITES.find(s => s.name.startsWith(prefix))?.name ?? prefix;

const AREAS: Area[] = [
    { area: 'Mparntwe Office – Main Building', site: site('Mparntwe'), type: 'WHS', weight: 5, risk: 0.7 },
    { area: 'Mparntwe Office – Tjanpi Warehouse', site: site('Mparntwe'), type: 'WHS', weight: 4, risk: 1 },
    { area: 'Pukatja – Program Office', site: site('Pukatja'), type: 'WHS', weight: 4, risk: 0.9 },
    { area: 'Warakurna – Program Office', site: site('Warakurna'), type: 'WHS', weight: 4, risk: 1 },
    { area: 'Amata – Aged Care Centre', site: site('Amata'), type: 'WHS', weight: 3, risk: 0.9 },
    { area: "Mutitjulu – Women's Centre", site: site('Mutitjulu'), type: 'WHS', weight: 3, risk: 1 },
    { area: 'Iwantja – Youth Centre', site: site('Iwantja'), type: 'WHS', weight: 2, risk: 1.2 },
    { area: 'Irrunytju – Community Office', site: site('Irrunytju'), type: 'WHS', weight: 2, risk: 1.1 },
    { area: 'Wanarn – Aged Care Centre', site: site('Wanarn'), type: 'WHS', weight: 1, risk: 1.9 },
    { area: 'Kaltukatjara – Program Office', site: site('Kaltukatjara'), type: 'WHS', weight: 0.6, risk: 2 },
    { area: 'Imanpa – Old Program Office', site: site('Imanpa'), type: 'WHS', weight: 2, risk: 1.4, archivedFrom: '2026-03' },
    { area: 'Fleet – LandCruiser NPY-03', site: site('Mparntwe'), type: 'Vehicle Check', weight: 3, risk: 0.8 },
    { area: 'Fleet – LandCruiser NPY-07', site: site('Pukatja'), type: 'Vehicle Check', weight: 3, risk: 1 },
    { area: 'Fleet – LandCruiser NPY-11', site: site('Warakurna'), type: 'Vehicle Check', weight: 3, risk: 1.1 },
    { area: 'Fleet – Troop Carrier NPY-14', site: site('Amata'), type: 'Vehicle Check', weight: 2, risk: 1.2 },
    { area: 'Fleet – HiLux NPY-19', site: site('Mutitjulu'), type: 'Vehicle Check', weight: 2, risk: 1 },
    { area: 'Warakurna – Staff House 2', site: site('Warakurna'), type: 'Site Inspection', weight: 2, risk: 1 },
    { area: 'Pukatja – Staff House 1', site: site('Pukatja'), type: 'Site Inspection', weight: 2, risk: 0.9 },
    { area: 'Amata – Staff Unit 3', site: site('Amata'), type: 'Site Inspection', weight: 2, risk: 1.1 },
    { area: 'Mimili – Staff House', site: site('Mimili'), type: 'Site Inspection', weight: 1, risk: 1.3 },
    { area: 'Amata – Aged Care Centre (Infection Control)', site: site('Amata'), type: 'Infection Control', weight: 2, risk: 0.9 },
    { area: 'Wanarn – Aged Care Centre (Infection Control)', site: site('Wanarn'), type: 'Infection Control', weight: 1, risk: 1.5 },
    { area: 'Pukatja – Day Respite Centre', site: site('Pukatja'), type: 'Infection Control', weight: 2, risk: 1 },
    { area: 'Amata – Community Kitchen', site: site('Amata'), type: 'Food Safety', weight: 2, risk: 1 },
    { area: 'Kaltjiti – Community Kitchen', site: site('Kaltjiti'), type: 'Food Safety', weight: 2, risk: 1.2 },
    { area: 'Mutitjulu – Community Kitchen', site: site('Mutitjulu'), type: 'Food Safety', weight: 2, risk: 1 },
    { area: 'Warakurna – Community Kitchen', site: site('Warakurna'), type: 'Food Safety', weight: 1, risk: 1.1 },
];

export interface Inspection {
    id: number;
    date: Date;
    type: AuditType;
    area: string;
    site: string;
    archived: boolean;
    score: number;
    failed: string[]; // question texts
    categoryScores: Record<string, number>;
}

/** Audits planned each month by type: [type, min, max]. */
const MONTHLY_PLAN: [AuditType, number, number][] = [
    ['WHS', 2, 3],
    ['Vehicle Check', 1, 2],
    ['Site Inspection', 0, 1],
    ['Infection Control', 0, 1],
    ['Food Safety', 0, 1],
];

const inspectionHistory = memo((): Inspection[] => {
    const n = now();
    const out: Inspection[] = [];
    let id = 7000;
    for (const m of historyMonths()) {
        const r = rng(`tendable:${m}`);
        const days = daysInMonth(m);
        const planned: { type: AuditType; day: number }[] = [];
        for (const [type, min, max] of MONTHLY_PLAN) {
            const count = r.int(min, max);
            for (let i = 0; i < count; i++) {
                // The first WHS audit each month lands in the first week.
                planned.push({ type, day: type === 'WHS' && i === 0 ? r.int(2, 6) : r.int(1, days) });
            }
        }
        planned.sort((a, b) => a.day - b.day);
        for (const p of planned) {
            const areas = AREAS.filter(a => a.type === p.type && (!a.archivedFrom || m < a.archivedFrom));
            const area = r.weighted(areas.map(a => [a, a.weight] as [Area, number]));
            const date = dateIn(m, p.day, r.int(8, 15), r.pick([0, 15, 30, 45]));
            const qs = QUESTIONS[p.type];
            const failed = qs.filter(x => r.bool(Math.min(0.9, x.fail * area.risk))).map(x => x.question);
            const cats = new Map<string, [number, number]>();
            for (const x of qs) {
                const [pass, tot] = cats.get(x.category) || [0, 0];
                cats.set(x.category, [pass + (failed.includes(x.question) ? 0 : 1), tot + 1]);
            }
            const categoryScores: Record<string, number> = {};
            cats.forEach(([pass, tot], c) => (categoryScores[c] = round1((pass / tot) * 100)));
            const rec: Inspection = {
                id: id++,
                date,
                type: p.type,
                area: area.area,
                site: area.site,
                archived: !!area.archivedFrom,
                score: round1(((qs.length - failed.length) / qs.length) * 100),
                failed,
                categoryScores,
            };
            if (date <= n) {
                out.push(rec);
            }
        }
    }
    return out;
});

export function inspections(): Inspection[] {
    return inspectionHistory('all');
}

function avg(values: number[]): number | null {
    return values.length ? round1(values.reduce((a, b) => a + b, 0) / values.length) : null;
}

/** The previous audit of the same area, if any. */
function previous(list: Inspection[], i: Inspection): Inspection | undefined {
    let prev: Inspection | undefined;
    for (const x of list) {
        if (x.area === i.area && x.type === i.type && x.date < i.date) {
            prev = x;
        }
    }
    return prev;
}

interface Action {
    inspection: Inspection;
    due: Date;
    completed: Date | null;
}

function actionsFor(list: Inspection[]): Action[] {
    const n = now();
    const out: Action[] = [];
    for (const i of list) {
        i.failed.forEach((question, k) => {
            const r = rng(`tendable:action:${i.id}:${k}`);
            const due = addDays(i.date, 30);
            // Most actions close inside a month; a few drag well past the due date.
            const done = addDays(i.date, r.bool(0.75) ? r.int(3, 28) : r.int(40, 160));
            out.push({ inspection: i, due, completed: done <= n ? done : null });
        });
    }
    return out;
}

function recurringFindings(all: Inspection[], scoped: Inspection[], s: Scope): Record<string, unknown>[] {
    const byQuestion = new Map<string, { occurrences: number; worst: number; areas: Set<string>; last: Date | null }>();
    const history = all.filter(i => i.date <= s.to);
    for (const i of scoped) {
        for (const question of i.failed) {
            const entry = byQuestion.get(question) || { occurrences: 0, worst: 0, areas: new Set<string>(), last: null };
            entry.occurrences++;
            if (!entry.last || i.date > entry.last) {
                entry.last = i.date;
            }
            // Streak of consecutive failures for this area ending at this audit.
            const areaHistory = history.filter(x => x.area === i.area && x.type === i.type && x.date <= i.date);
            let streak = 0;
            for (let k = areaHistory.length - 1; k >= 0 && areaHistory[k].failed.includes(question); k--) {
                streak++;
            }
            if (streak >= 2) {
                entry.areas.add(i.area);
            }
            entry.worst = Math.max(entry.worst, streak);
            byQuestion.set(question, entry);
        }
    }
    return [...byQuestion.entries()]
        .filter(([, e]) => e.worst >= 2)
        .sort((a, b) => b[1].worst - a[1].worst || b[1].occurrences - a[1].occurrences || a[0].localeCompare(b[0]))
        .map(([question, e]) => ({
            question,
            occurrences: e.occurrences,
            worstStreak: e.worst,
            areas: [...e.areas].sort(),
            lastSeen: e.last ? iso(e.last) : null,
        }));
}

function knownType(type: string | null): AuditType | null {
    return type && (AUDIT_TYPES as readonly string[]).includes(type) ? (type as AuditType) : null;
}

/** GET /api/tendable/stats[?type=]. Board twin returns the same keys. */
export function tendableStats(q: Quarter | null, type: string | null = null, board = false): Record<string, unknown> {
    void board;
    const s = scopeOf(q);
    const all = inspections();
    const list = all.filter(i => inScope(i.date, s));
    const typed = type ? list.filter(i => i.type === type) : list;
    const categoryType = knownType(type) ?? 'WHS';
    const forCategories = list.filter(i => i.type === categoryType);

    const byType = AUDIT_TYPES.map(t => {
        const rows = list.filter(i => i.type === t);
        return { type: t, count: rows.length, averageScore: avg(rows.map(i => i.score)) };
    }).filter(r => r.count > 0 || r.type === 'WHS');

    const sites = new Map<string, number[]>();
    typed.forEach(i => sites.set(i.site, [...(sites.get(i.site) || []), i.score]));
    const bySite = [...sites.entries()]
        .map(([site, scores]) => ({ site, count: scores.length, averageScore: avg(scores) }))
        .sort((a, b) => b.count - a.count || a.site.localeCompare(b.site));

    // The trend chart carries the twelve months up to the scope's end.
    const ts = trendScopeOf(q);
    const trendList = all.filter(i => inScope(i.date, ts) && (!type || i.type === type));
    const monthly: Record<string, unknown>[] = [];
    const typedMonths = trendList.map(i => ym(i.date)).sort();
    if (typedMonths.length) {
        const first = typedMonths[0];
        for (const m of ts.months.filter(x => x >= first)) {
            const rows = trendList.filter(i => ym(i.date) === m);
            monthly.push({ month: m, count: rows.length, averageScore: avg(rows.map(i => i.score)) });
        }
    }

    const cats = new Map<string, number[]>();
    forCategories.forEach(i => Object.entries(i.categoryScores).forEach(([c, v]) => cats.set(c, [...(cats.get(c) || []), v])));
    const categoryAverages = [...cats.entries()]
        .map(([category, scores]) => ({ category, averageScore: avg(scores), inspections: scores.length }))
        .sort((a, b) => (a.averageScore ?? 0) - (b.averageScore ?? 0) || a.category.localeCompare(b.category));

    let fresh = 0;
    let repeat = 0;
    let resolved = 0;
    for (const i of list) {
        const prev = previous(all, i);
        for (const question of i.failed) {
            if (prev && prev.failed.includes(question)) repeat++;
            else fresh++;
        }
        if (prev) {
            resolved += prev.failed.filter(question => !i.failed.includes(question)).length;
        }
    }
    const actions = actionsFor(list);
    const n = now();
    const open = actions.filter(a => !a.completed);

    return {
        total: list.length,
        average_score: avg(list.map(i => i.score)),
        by_type: byType,
        by_site: bySite,
        monthly_trend: monthly,
        category_averages: categoryAverages,
        recurring_findings: recurringFindings(all, typed, s),
        issues: { new: fresh, repeat, resolved },
        actions: { open: open.length, overdue: open.filter(a => a.due < n).length, completed: actions.length - open.length },
        archived_area_inspections: list.filter(i => i.archived).length,
        last_synced_at: syncedAt('tendable'),
    };
}

/** GET /api/tendable/coverage[?type=] — latest audit per area, oldest first. */
export function tendableCoverage(q: Quarter | null, type: string | null = null): Record<string, unknown> {
    const s = scopeOf(q);
    const t = type || 'WHS';
    const latest = new Map<string, Inspection>();
    for (const i of inspections()) {
        if (i.type === t && !i.archived && i.date <= s.to) {
            const prev = latest.get(i.area);
            if (!prev || i.date > prev.date) {
                latest.set(i.area, i);
            }
        }
    }
    const areas = [...latest.values()]
        .sort((a, b) => a.date.getTime() - b.date.getTime())
        .map(i => ({ area: i.area, site: i.site, lastInspected: iso(i.date), type: i.type, score: i.score }));
    return { type: t, areas };
}

/** WHS audits in scope for the CEO report ("6 audits, avg 91.5%"). */
export function whsAuditSummary(q: Quarter | null): { audits: number; averageScore: number | null } {
    const s = scopeOf(q);
    const rows = inspections().filter(i => i.type === 'WHS' && inScope(i.date, s));
    return { audits: rows.length, averageScore: avg(rows.map(i => i.score)) };
}
